import { duration, formatDate, now } from "common";
import { useMemo, useRef } from "react";
import { useQuery } from "react-query";
import { DatepickerPreference, Timeslot } from "types";
import { api } from "../../api/api";
import { useCreatePurchaseStore } from "@/stores/createPurchaseStore";

/**
 * Let's assume it's April now and there are no slots for April and May (a rough 30-day range)
 * If there are ZERO slots found, we should fetch upcoming dates. Let's assume this returns dates in August and December
 * We will show at max SIX upcoming dates in the date picker. In the date picker if the user now selects a date AFTER
 * August (the first batch), for example October, then we re-fetch the upcoming dates, and show only the December dates.
 *
 * Another scenario:
 * If there are <= 3 **dates** (not timeslots) in the next 30 days, then we will still fetch and display upcoming dates
 *
 * Here is the v1 logic for the upcoming trigger https://github.com/xola/xola/blob/master/assets/js/payment/view/arrivalDateSelector.js#L936-L938
 */
export const useUpcomingDates = (
    timeslots: Timeslot[],
    quantity: number,
    startDate: string,
    productId: string,
    isOngoingRequest: boolean,
) => {
    const next30Days = formatDate(now(startDate).add(30, "days"));
    const availableDates = useRef(new Set()); // To track dates, and not timeslots

    useMemo(() => {
        // Using string comparison here because using dayjs on 1k+ timeslots will be slow
        for (const slot of timeslots) {
            if (slot.date <= next30Days && slot.capacity.open >= quantity) {
                availableDates.current.add(slot.date);
            }
        }
    }, [timeslots, quantity, next30Days]);

    const shouldFetchUpcoming = !isOngoingRequest && availableDates.current.size <= 3;
    const query = useQuery(
        // Named this way so that invalidating timeslots, will invalidate the upcoming cache too
        ["timeslots", "collection", "upcoming", productId, startDate],
        async () => {
            const params = startDate ? { limit: 6, startDate } : { limit: 6 };
            const { data } = await api.get<string[]>(`/experiences/${productId}/upcoming`, { params });
            return data;
        },
        {
            enabled: shouldFetchUpcoming,
            staleTime: duration(30, "seconds"),
            keepPreviousData: true,
            onSuccess: (data) => {
                handleUpcomingDatesFetched(data, timeslots);
            },
        },
    );

    return { ...query, upcomingDates: query.data, shouldFetchUpcoming };
};

const handleUpcomingDatesFetched = (data: string[], timeslots: Timeslot[]) => {
    // If there are no timeslots or fewer than 3 dates from useArrivalTimeslots, we fetch upcoming dates using useUpcomingDates.
    // If both upcoming dates and timeslots are empty, show ProductAvailabilityAlert by setting hasAvailableTimeslot to false.
    if (data.length === 0 && (timeslots.length === 0 || !timeslots.some((slot) => slot.canBook))) {
        useCreatePurchaseStore.setState({ hasAvailableTimeslot: false });
    }
};

export const useLastScheduledDate = (product: ID, datePickerPreference: DatepickerPreference) => {
    const isEnabled = !!datePickerPreference.unavailableScheduleMessage;
    return useQuery(
        // Named this way so that invalidating timeslots, will invalidate the lastScheduledDate cache too
        ["timeslots", "collection", "lastScheduledDate", product],
        async () => {
            const data = await api.get("/timeslots", { params: { product } });
            return data.headers["last-scheduled-date"];
        },
        { enabled: isEnabled, staleTime: duration(60, "seconds") }, // Only needed if the message is present
    );
};
